/**
 * انتخابِ طرف‌حساب (تأمین‌کننده/بافنده/رنگرز/انبار/سایر) + «افزودنِ شرکتِ جدید» از همان‌جا.
 *
 * از v2.125 هر مرحله می‌تواند چند شرکت داشته باشد (مثلاً نخ از جای دیگری جز درین، رنگرزیِ
 * دوم کنارِ پوشان). خالی‌گذاشتنِ فیلدی که `defaultHint` دارد یعنی «پیش‌فرض» — سرور قدیمی‌ترین
 * طرفِ فعالِ همان نوع را می‌گذارد، پس فرمِ تازه بی‌جهت «تغییرکرده» نمی‌شود.
 */
import React, { useMemo, useState } from "react";
import { Select, Button, Divider, Modal, Form, Input, Switch, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { scCreateParty, scUpdateParty } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { PARTY_TYPE_LABEL } from "./scConstants";

export const PARTY_TYPE_OPTIONS = Object.entries(PARTY_TYPE_LABEL).map(([value, label]) => ({ value, label }));

/** مودالِ ساخت/ویرایشِ طرف‌حساب — مشترکِ PartyPicker و صفحهٔ «طرف‌حساب‌ها». */
export function PartyFormModal({ open, initial, defaultType, lockType, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  const initialValues = initial?.id
    ? { name: initial.name, party_type: initial.party_type, notes: initial.notes, is_active: initial.is_active }
    : { name: initial?.name || "", party_type: defaultType || "supplier", notes: "", is_active: true };

  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      let id = initial?.id;
      if (editing) await scUpdateParty(id, v);
      else id = (await scCreateParty(v)).data.id;
      message.success(editing ? "طرف حساب ویرایش شد" : `«${v.name}» اضافه شد`);
      onSaved?.({ id, ...v });
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در ذخیره");
    }
    setSaving(false);
  };

  return (
    <Modal title={editing ? `ویرایشِ ${initial.name}` : "افزودنِ طرف حساب / شرکتِ جدید"}
      open={open} onCancel={onClose} onOk={save} confirmLoading={saving}
      okText={editing ? "ذخیره" : "افزودن"} cancelText="انصراف" destroyOnHidden>
      <Form form={form} layout="vertical" style={{ direction: "rtl" }} preserve={false} initialValues={initialValues}>
        <Form.Item name="name" label="نامِ شرکت / طرف حساب" rules={[{ required: true, whitespace: true, message: "نام الزامی است" }]}>
          <Input autoFocus placeholder="مثلاً: ریسندگی البرز" />
        </Form.Item>
        <Form.Item name="party_type" label="نقش در زنجیره" rules={[{ required: true }]}
          extra="تأمین‌کننده = فروشندهٔ نخ/مواد؛ بافندگی/رنگرزی = پیمانکارِ آن مرحله؛ سایر = کارتن، چاپ، لیبل و …">
          <Select options={PARTY_TYPE_OPTIONS} disabled={lockType && !editing} />
        </Form.Item>
        <Form.Item name="notes" label="توضیح (چه چیزی تأمین می‌کند، تلفن، …)">
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
        {editing && (
          <Form.Item name="is_active" label="فعال" valuePropName="checked"
            extra="غیرفعال در فهرستِ انتخاب نمی‌آید ولی سوابقش می‌ماند.">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

/**
 * Select طرف‌حساب. `value`/`onChange` را Form.Item تزریق می‌کند.
 * `types`: نقش‌های مجاز (null = همه). `onCreated(party)`: صفحه لیستِ خودش را به‌روز کند.
 */
export default function PartyPicker({
  value, onChange, types, parties = [], onCreated, defaultHint, size = "large",
  placeholder = "انتخاب کنید", allowClear = true, style,
}) {
  const { can } = useAuth();
  const [adding, setAdding] = useState(null);   // متنِ جست‌وجو هنگامِ بازکردنِ «افزودن»
  const [search, setSearch] = useState("");
  const [dropOpen, setDropOpen] = useState(false);  // بسته‌شدنِ فهرست قبل از بازشدنِ مودال (وگرنه رویش می‌ماند)

  const options = useMemo(() => parties
    .filter((p) => (!types || types.includes(p.party_type)) && (p.is_active !== false || p.id === value))
    .sort((a, b) => a.id - b.id)
    .map((p) => ({
      value: p.id,
      label: p.is_active === false ? `${p.name} (غیرفعال)` : p.name,
    })), [parties, types, value]);

  // پیش‌فرضِ سرور = قدیمی‌ترین طرفِ فعالِ همان نوع
  const fallback = defaultHint && options.length ? options[0].label : null;
  const canAdd = can("sc.party.manage");

  return (
    <>
      <Select
        value={value ?? undefined} onChange={(v) => onChange?.(v ?? null)} size={size} style={style}
        showSearch optionFilterProp="label" allowClear={allowClear} options={options}
        searchValue={search} onSearch={setSearch} open={dropOpen} onOpenChange={setDropOpen}
        placeholder={fallback ? `پیش‌فرض: ${fallback}` : placeholder}
        notFoundContent={canAdd ? "یافت نشد — از پایینِ فهرست اضافه کنید" : "یافت نشد"}
        popupRender={(menu) => (
          <>
            {menu}
            {canAdd && (
              <>
                <Divider style={{ margin: "6px 0" }} />
                <Button type="link" icon={<PlusOutlined />} block style={{ textAlign: "right" }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { setDropOpen(false); setAdding(search || ""); }}>
                  {search ? `افزودنِ «${search}» به‌عنوانِ شرکتِ جدید` : "افزودنِ شرکت / طرف حسابِ جدید"}
                </Button>
              </>
            )}
          </>
        )}
      />
      <PartyFormModal
        open={adding !== null}
        initial={{ name: adding || "" }}
        defaultType={types?.[0] || "supplier"}
        lockType={!!types && types.length === 1}
        onClose={() => setAdding(null)}
        onSaved={(p) => {
          setAdding(null);
          setSearch("");
          const full = { is_active: true, event_count: 0, ...p };
          onCreated?.(full);
          if (!types || types.includes(p.party_type)) onChange?.(p.id);
        }}
      />
    </>
  );
}
